/**
 * An entity's analyst facts in its detail (PRD-0075 R-C3, R-C4, SC-012, SC-013): the properties an
 * analyst asserted, each with the "analyst assertion" badge and its provenance; the properties no
 * longer valid (hidden, or greyed with the toggle, each with "valid again"); a retirement and its
 * undo; and the three actions. Presentational: given the model `entityFactsModel` reads off the
 * node's properties.
 */
import { Ban, CalendarX2, Pin, RotateCcw, Undo2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { valueText } from '@/features/entity-history/summary-view'
import { formatDate } from '@/utils/date'
import type {
  AssertionView,
  EntityFactsModel,
  InvalidPropertyView,
  RetirementView,
} from './fact-marks'

const T = 'analystFacts'

export interface EntityFactActions {
  onAddFact: () => void
  onInvalidate: () => void
  onRevalidate: (key: string) => void
  onRetire: () => void
  onRestore: () => void
}

export interface EntityFactsViewProps {
  model: EntityFactsModel
  showNoLongerValid: boolean
  actions: EntityFactActions
  pending: boolean
}

function useDay() {
  const { i18n } = useTranslation()
  return (iso: string | null) => (iso ? formatDate(iso, i18n.language) : '')
}

export function AssertionBadge() {
  const { t } = useTranslation()
  return (
    <Badge variant="info" title={t(`${T}.badgeHint`)}>
      <Pin aria-hidden data-icon="inline-start" />
      {t(`${T}.badge`)}
    </Badge>
  )
}

function Assertion({ assertion }: { assertion: AssertionView }) {
  const { t } = useTranslation()
  const day = useDay()
  const source = assertion.source?.documentName ?? assertion.source?.text
  const details = [
    t(`${T}.assertedBy`, {
      name: assertion.byName ?? t(`${T}.unknownAnalyst`),
      date: day(assertion.at),
    }),
    source ? t(`${T}.source`, { source }) : null,
    assertion.validFrom || assertion.validTo
      ? t(`${T}.validity`, {
          from: day(assertion.validFrom) || '…',
          to: day(assertion.validTo) || '…',
        })
      : null,
  ].filter(Boolean)
  return (
    <li data-asserted-key={assertion.key} className="flex flex-col gap-0.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-muted-foreground">{assertion.key}:</span>
        <span className="break-words font-medium text-foreground">
          {valueText(assertion.value) ?? '—'}
        </span>
        <AssertionBadge />
      </div>
      <p className="text-3xs text-muted-foreground">{details.join(' · ')}</p>
    </li>
  )
}

function InvalidProperty({
  property,
  onRevalidate,
  pending,
}: {
  property: InvalidPropertyView
  onRevalidate: (key: string) => void
  pending: boolean
}) {
  const { t } = useTranslation()
  const day = useDay()
  return (
    <li data-invalid-key={property.key} className="flex items-start gap-2 opacity-60">
      <CalendarX2 aria-hidden className="mt-0.5 size-3 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1 break-words">
        {t(`${T}.invalidProperty`, {
          key: property.key,
          value: valueText(property.value) ?? '—',
          date: day(property.invalidation.from),
          reason: property.invalidation.reason ?? '',
        })}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="xs"
        disabled={pending}
        onClick={() => onRevalidate(property.key)}
      >
        <Undo2 aria-hidden />
        {t(`${T}.actions.validAgain`)}
      </Button>
    </li>
  )
}

function Retired({
  retirement,
  onRestore,
  pending,
}: {
  retirement: RetirementView
  onRestore: () => void
  pending: boolean
}) {
  const { t } = useTranslation()
  const day = useDay()
  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-2 py-1.5"
    >
      <Ban aria-hidden className="size-3.5 shrink-0 text-destructive" />
      <span className="min-w-0 flex-1 break-words text-foreground/85">
        {t(`${T}.retiredBanner`, {
          name: retirement.byName ?? t(`${T}.unknownAnalyst`),
          date: day(retirement.at),
          reason: retirement.reason ?? '',
        })}
      </span>
      <Button type="button" variant="outline" size="xs" disabled={pending} onClick={onRestore}>
        <RotateCcw aria-hidden />
        {t(`${T}.actions.restore`)}
      </Button>
    </div>
  )
}

function Actions({ actions, pending }: { actions: EntityFactActions; pending: boolean }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Button
        type="button"
        variant="outline"
        size="xs"
        disabled={pending}
        onClick={actions.onAddFact}
      >
        <Pin aria-hidden />
        {t(`${T}.actions.addFact`)}
      </Button>
      <Button
        type="button"
        variant="outline"
        size="xs"
        disabled={pending}
        onClick={actions.onInvalidate}
      >
        <CalendarX2 aria-hidden />
        {t(`${T}.actions.noLongerValid`)}
      </Button>
      <Button type="button" variant="ghost" size="xs" disabled={pending} onClick={actions.onRetire}>
        <Ban aria-hidden />
        {t(`${T}.actions.markWrong`)}
      </Button>
    </div>
  )
}

export function EntityFactsView({
  model,
  showNoLongerValid,
  actions,
  pending,
}: EntityFactsViewProps) {
  const { t } = useTranslation()
  const hidden = model.invalid.length
  return (
    <section aria-label={t(`${T}.section`)} className="flex flex-col gap-1.5 text-2xs">
      {model.retirement && (
        <Retired retirement={model.retirement} onRestore={actions.onRestore} pending={pending} />
      )}
      {model.assertions.length > 0 && (
        <ul aria-label={t(`${T}.asserted`)} className="flex flex-col gap-1">
          {model.assertions.map(assertion => (
            <Assertion key={assertion.key} assertion={assertion} />
          ))}
        </ul>
      )}
      {hidden > 0 &&
        (showNoLongerValid ? (
          <ul aria-label={t(`${T}.noLongerValid`)} className="flex flex-col gap-1">
            {model.invalid.map(property => (
              <InvalidProperty
                key={property.key}
                property={property}
                onRevalidate={actions.onRevalidate}
                pending={pending}
              />
            ))}
          </ul>
        ) : (
          <p className="text-muted-foreground">{t(`${T}.hiddenProperties`, { count: hidden })}</p>
        ))}
      {!model.retirement && <Actions actions={actions} pending={pending} />}
    </section>
  )
}
